import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";

import { Box, Container, Grid, Link, Typography } from "@mui/material";
import SideBar from "../../components/Setting/Sidebar";
import { DeveloperHeader } from "../../components/DeveloperHeader";
import { useDeveloperContext } from "../../hook/useDeveloperContext";
import { useEffect, useState } from "react";
import { developerApi } from "../../openApi";
import { HTTP_RESPONSE_CODE } from "../../common/const";
import { toast } from "react-toastify";
import { validateFields } from "../../common/helper";
import * as yup from "yup";
import { useRecoilState } from "recoil";
import { ShowProgress } from "../../state/ShowProgress";
import { useNavigate } from "react-router-dom";
import { ThemeLight } from "../../state/ThemeLight";

const MySetting = () => {
  const navigate = useNavigate();

  const { username, sessionKey, userID, handleSessionKey, handleUserEmail } =
    useDeveloperContext();

  const [, setShowProgress] = useRecoilState(ShowProgress);
  const [themeLight] = useRecoilState(ThemeLight);

  const [email, setEmail] = useState<string>("");
  const [subscribeNewsletter, setSubscribeNewsletter] =
    useState<boolean>(false);

  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

  useEffect(() => {
    if (sessionKey !== undefined && sessionKey !== "" && userID !== 0) {
      developerApi
        .getInformation(userID.toString(), sessionKey)
        .then((res) => {
          setEmail(res.data.email);
          setSubscribeNewsletter(res.data.subscribeNewsletter);
        })
        .catch((err) => {
          if (err.response) {
            if (
              err.response.data.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED
            ) {
              toast.error("Session key is expired", {
                theme: themeLight ? "light" : "dark",
              });
              handleSessionKey("");
            } else {
              toast.error(err.response.data.message, {
                theme: themeLight ? "light" : "dark",
              });
            }
          } else {
            toast.error(err.message, { theme: themeLight ? "light" : "dark" });
          }
        });
    }
  }, [sessionKey, userID]);

  const handleUpdateProfile = async () => {
    if (!(await validateUpdateProfile())) {
      return;
    }

    setShowProgress(true);

    developerApi
      .updateInformation(userID.toString(), {
        email,
        subscribeNewsletter,
        accessToken: sessionKey!,
      })
      .then((res) => {
        setShowProgress(false);

        handleUserEmail(email);

        toast.success("Account information has been updated successfully.", {
          theme: themeLight ? "light" : "dark",
        });
      })
      .catch((err) => {
        setShowProgress(false);
        if (err.response) {
          if (
            err.response.data.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED
          ) {
            toast.error("Session key is expired", {
              theme: themeLight ? "light" : "dark",
            });
            handleSessionKey("");
          } else {
            toast.error(err.response.data.message, {
              theme: themeLight ? "light" : "dark",
            });
          }
        } else {
          toast.error(err.message, { theme: themeLight ? "light" : "dark" });
        }
      });
  };

  const validateUpdateProfile = async () => {
    try {
      await validateFields(
        yup.object().shape({
          email: yup
            .string()
            .required("Please input the email addres.")
            .email("Please input the correct email address."),
        }),
        ["email"],
        [email]
      );
    } catch (err: any) {
      toast.warn(err.message, { theme: themeLight ? "light" : "dark" });
      return false;
    }

    return true;
  };

  const handleUpdatePassword = async () => {
    if (!(await validateUpdatePassword())) {
      return;
    }

    setShowProgress(true);
    developerApi
      .updateInformation(userID.toString(), {
        currentPassword: currentPassword,
        password: newPassword,
        accessToken: sessionKey!,
      })
      .then((res) => {
        setShowProgress(false);

        toast.success("Password has been updated successfully.", {
          theme: themeLight ? "light" : "dark",
        });
      })
      .catch((err) => {
        setShowProgress(false);
        if (err.response) {
          if (
            err.response.data.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED
          ) {
            toast.error("Session key is expired", {
              theme: themeLight ? "light" : "dark",
            });
            handleSessionKey("");
          } else {
            toast.error(err.response.data.message, {
              theme: themeLight ? "light" : "dark",
            });
          }
        } else {
          toast.error(err.message, { theme: themeLight ? "light" : "dark" });
        }
      });
  };

  const validateUpdatePassword = async () => {
    try {
      await validateFields(
        yup.object().shape({
          currentPassword: yup
            .string()
            .required("Please input the current password."),
          newPassword: yup.string().required("Please input the new password."),
          confirmPassword: yup
            .string()
            .required("Please input the confirm password."),
        }),
        ["currentPassword", "newPassword", "confirmPassword"],
        [currentPassword, newPassword, confirmPassword]
      );

      if (newPassword !== confirmPassword) {
        throw new Error("Please input the correct confirm password.");
      }

      if (currentPassword === newPassword) {
        throw new Error("Current password and new password are the same.");
      }
    } catch (err: any) {
      toast.warn(err.message, { theme: themeLight ? "light" : "dark" });
      return false;
    }

    return true;
  };

  const handleDeleteAccount = () => {
    if (!confirmDelete) {
      toast.warn("Please check the confirmation.", {
        theme: themeLight ? "light" : "dark",
      });
      return;
    }

    setShowProgress(true);
    developerApi
      .deleteDeveloper(userID.toString(), {
        accessToken: sessionKey!,
      })
      .then((res) => {
        setShowProgress(false);

        if (res) {
          toast.success("Account has been deleted successfully.", {
            theme: themeLight ? "light" : "dark",
          });
          handleSessionKey(undefined);

          navigate("/login");
        } else {
          toast.error("Deleteing account has been failed.", {
            theme: themeLight ? "light" : "dark",
          });
        }
      })
      .catch((err) => {
        setShowProgress(false);
        if (err.response) {
          if (
            err.response.data.statusCode === HTTP_RESPONSE_CODE.UNAUTHORIZED
          ) {
            toast.error("Session key is expired", {
              theme: themeLight ? "light" : "dark",
            });
            handleSessionKey("");
          } else {
            toast.error(err.response.data.message, {
              theme: themeLight ? "light" : "dark",
            });
          }
        } else {
          toast.error(err.message, { theme: themeLight ? "light" : "dark" });
        }
      });
  };

  return (
    <>
      <DeveloperHeader title="Account Settings" />

      <Container className="container-xxl pb-16 mt-n14">
        <Grid container spacing={2}>
          <Grid item xl={3} lg={4}>
            <SideBar />
          </Grid>
          <Grid item xl={9} lg={8}>
            <Box className="card mb-4">
              <Box className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-header-title">User Settings</h3>
              </Box>
              <Box className="card-body">
                <p>
                  Below are the username, email and overview information for
                  your account.
                </p>
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <AccountCircleOutlinedIcon className="text-icon" /> Your
                    username:
                  </Box>
                  <Box className="col-md-8">
                    <strong>{username}</strong>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <EmailOutlinedIcon className="text-icon" /> Your Email
                    Address:
                  </Box>
                  <Box className="col-md-8">
                    <Box className="d-flex justify-content-between align-items-center my-n1">
                      <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="form-control py-2"
                      />
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-center">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <ExitToAppIcon className="text-icon" /> Newsletter:
                  </Box>
                  <Box className="col-md-8">
                    <Box className="form-check">
                      <input
                        name="ctl00$ContentPlaceHolder1$chkNewsletter"
                        type="checkbox"
                        id="ContentPlaceHolder1_chkNewsletter"
                        className="form-check-input"
                        checked={subscribeNewsletter}
                        onChange={(e) =>
                          setSubscribeNewsletter(e.target.checked)
                        }
                      />
                      <label className="form-check-label">
                        <Box className="text-dark fw-medium mb-1">
                          Subscribe to Newsletter
                        </Box>
                        <Box className="text-muted small">
                          UPCX Explorer's monthly newsletter brings you the
                          latest features, analyses, trending stories, community
                          contributions, job listings and giveaways!
                        </Box>
                      </label>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box className="card-footer bg-light d-md-flex justify-content-end gap-2">
                <Link className="btn btn-ghost-white" href="/myaccount">
                  Cancel
                </Link>
                <input
                  type="submit"
                  name="ctl00$ContentPlaceHolder1$btnSave"
                  value="Save Changes"
                  id="ContentPlaceHolder1_btnSave"
                  className="btn btn-primary"
                  style={{ display: "none" }}
                />
                <input
                  type="button"
                  className="btn btn-primary"
                  value="Save Changes"
                  onClick={handleUpdateProfile}
                />
              </Box>
            </Box>

            <Box className="card mb-4">
              <Box className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-header-title">Password</h3>
              </Box>
              <Box className="card-body">
                <p className="text-dark mb-4">
                  Edit the fields below to update your password.
                </p>
                <Box className="row align-items-baseline">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <LockOutlinedIcon className="text-icon" /> Enter OLD
                    password
                  </Box>
                  <Box className="col-md-8">
                    <Box className="position-relative">
                      <input
                        name="ctl00$ContentPlaceHolder1$txtOldPassword"
                        id="ContentPlaceHolder1_txtOldPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        placeholder="*************"
                        className="js-toggle-password form-control py-2"
                        style={{ paddingRight: "3rem" }}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                      />
                      <span
                        className="content-center position-absolute top-0 py-1"
                        title="Password Toggle"
                        style={{ right: "0.3rem" }}
                      >
                        <button
                          type="button"
                          className="js-change-password-multi-1 btn btn-sm btn-ghost-white fs-70x"
                          style={{ width: "2rem", height: "2rem" }}
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                        >
                          <RemoveRedEyeOutlinedIcon
                            id="showMultiPassIcon1"
                            className="text-icon"
                          />
                        </button>
                      </span>
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-baseline">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <LockOutlinedIcon className="text-icon" /> Enter NEW
                    password
                  </Box>
                  <Box className="col-md-8">
                    <Box className="position-relative mb-2">
                      <input
                        name="ctl00$ContentPlaceHolder1$txtPassword"
                        id="ContentPlaceHolder1_txtPassword"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="*************"
                        className="js-toggle-password form-control py-2"
                        style={{ paddingRight: "3rem" }}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <span
                        className="content-center position-absolute top-0 py-1"
                        title="Password Toggle"
                        style={{ right: "0.3rem" }}
                      >
                        <button
                          type="button"
                          className="js-change-password-multi-2 btn btn-sm btn-ghost-white fs-70x"
                          style={{ width: "2rem", height: "2rem" }}
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          <RemoveRedEyeOutlinedIcon
                            id="showMultiPassIcon2"
                            className="text-icon"
                          />
                        </button>
                      </span>
                    </Box>
                  </Box>
                </Box>
                <hr />
                <Box className="row align-items-baseline">
                  <Box className="col-md-4 text-muted mb-1 mb-md-0">
                    <LockOutlinedIcon className="text-icon" /> Re-Confirm
                    password
                  </Box>
                  <Box className="col-md-8">
                    <Box className="position-relative">
                      <input
                        name="ctl00$ContentPlaceHolder1$txtPassword"
                        id="ContentPlaceHolder1_txtPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="*************"
                        className="js-toggle-password form-control py-2"
                        style={{ paddingRight: "3rem" }}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                      <span
                        className="content-center position-absolute top-0 py-1"
                        title="Password Toggle"
                        style={{ right: "0.3rem" }}
                      >
                        <button
                          type="button"
                          className="js-change-password-multi-3 btn btn-sm btn-ghost-white fs-70x"
                          style={{ width: "2rem", height: "2rem" }}
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          <RemoveRedEyeOutlinedIcon
                            id="showMultiPassIcon3"
                            className="text-icon"
                          />
                        </button>
                      </span>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className="card-footer bg-light d-md-flex justify-content-end gap-2">
                <Link className="btn btn-ghost-white" href="/myaccount">
                  Cancel
                </Link>
                <input
                  type="submit"
                  name="ctl00$ContentPlaceHolder1$btnSave"
                  value="Save Changes"
                  id="ContentPlaceHolder1_btnSave"
                  className="btn btn-primary"
                  style={{ display: "none" }}
                />
                <input
                  type="button"
                  className="btn btn-primary"
                  value="Save Changes"
                  onClick={handleUpdatePassword}
                />
              </Box>
            </Box>

            <Box className="card mb-4">
              <Box className="card-header d-flex justify-content-between align-items-center">
                <h3 className="card-header-title">Delete Account</h3>
              </Box>
              <Box className="card-body">
                <p className="fw-medium">
                  Are you sure you want to permanently delete your user account?
                </p>
                <p>
                  Deleting your user account will also delete all the
                  watchlists, transaction notes, private tags and verified
                  addresses ownership. Recovery of the above is not possible
                  upon delete confirmation.
                </p>
              </Box>
              <Box className="card-footer bg-light d-flex flex-wrap align-items-center justify-content-between gap-3">
                <Box className="form-check">
                  <input
                    name="ctl00$ContentPlaceHolder1$deleteAccountCheckbox"
                    type="checkbox"
                    id="ContentPlaceHolder1_deleteAccountCheckbox"
                    className="form-check-input"
                    data-gtm-form-interact-field-id="0"
                    checked={confirmDelete}
                    onChange={(e) => setConfirmDelete(e.target.checked)}
                  />
                  <label className="form-check-label">
                    Confirm that I want to delete my account.
                  </label>
                </Box>
                <Box className="text-nowrap">
                  <input
                    type="submit"
                    name="ctl00$ContentPlaceHolder1$btnDelete"
                    value="Delete Account"
                    id="ContentPlaceHolder1_btnDelete"
                    className="btn btn-danger"
                    onClick={handleDeleteAccount}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default MySetting;
